import templateUrl from "./user-security-editor.component.html";
import "./user-security-editor.component.less";
import { UserPermissionEditorModal } from "./user-permission-editor-modal.component";

// eslint-disable-next-line @typescript-eslint/naming-convention
const GrantType = {
    GrantAll: "GrantAll",
    GrantRole: "GrantRole",
    GrantClaims: "GrantClaim",
    None: "None"
}

const roleClaimType = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

export const UserSecurityEditorComponent = {
    templateUrl,
    require: {
        employeeForm: "^^?form"
    },
    bindings: {
        user: "<",
        roles: "<",
        icdOffices: "<",
        divisions: "<",
        permissionDescriptions: "<",
        claimPermissions: "<",
        userPermissions: "<"
    },
    controller: class {
        constructor($uibModal, $state, employeeService, utilityService, permissionGrantService) {
            'ngInject';
            this.$uibModal = $uibModal;
            this.$state = $state;
            this.employeeService = employeeService;
            this.utilityService = utilityService;
            this.permissionGrantService = permissionGrantService;
        }

        $onInit() {
            if (!this.employeeForm) {
                // Note, employeeForm is the parent form, whereas editForm is the local form.
                window.onbeforeunload = () => {
                    if (this.editForm && this.editForm.$dirty) {
                        return "There are unsaved changes. Are you sure you want to abandon them?"
                    }
                }
            }
        }

        $onChanges(changesObj) {
            if (changesObj.user) {
                this.pristineClaims = this.reduceClaimsFromUserRoles();
                this.claims = this.pristineClaims;
                this.pristineSecurity = {
                    Roles: this.user.Roles.filter(p => this.roles.some(q => q.RoleID === p && q.Scope === "Global")),
                    RolesAtDivisions: this.user.RolesAtDivisions.filter(p => this.roles.some(q => q.RoleID === p.RoleID && q.Scope === "Divisional")),
                    RolesAtOffices: this.user.RolesAtOffices.filter(p => this.roles.some(q => q.RoleID === p.RoleID && q.Scope === "Organizational"))
                };
            }
        }

        onSave() {
            const patch = this._generateSecurityPatch();
            if (patch.roles || patch.rolesAtDivisions || patch.rolesAtOffices) {
                const saving = this.employeeService.updateEmployeeSecuritySettings(this.user.EmployeeID, patch);
                this.utilityService.showLoadingUntilResolved(
                    this.utilityService.alertOnError(saving, "Saving roles failed")
                );
                saving.then(() => {
                    this.pristineSecurity = {
                        Roles: this.user.Roles.filter(p => this.roles.some(q => q.RoleID === p && q.Scope === "Global")),
                        RolesAtDivisions: this.user.RolesAtDivisions.filter(p => this.roles.some(q => q.RoleID === p.RoleID && q.Scope === "Divisional")),
                        RolesAtOffices: this.user.RolesAtOffices.filter(p => this.roles.some(q => q.RoleID === p.RoleID && q.Scope === "Organizational"))
                    };
                    this.editForm.$setPristine(true);
                });
            }
        }

        onUserRolesChanged({ roles, rolesAtDivisions, rolesAtOffices }) {
            this.user.Roles = roles;
            this.user.RolesAtDivisions = rolesAtDivisions;
            this.user.RolesAtOffices = rolesAtOffices;
            this.claims = this.reduceClaimsFromUserRoles();
            if (this.editForm) {
                this.editForm.$setDirty();
            }
        }

        onAddUserPermission({ key } = { key: null }) {
            this._showEditUserPermissionModal({
                Key: key,
                Value: true
            });
        }

        onUpdateUserPermission({ userPermission }) {
            const grant = {
                Key: userPermission.Description.Key,
                Value: userPermission.Value
            };
            this._showEditUserPermissionModal(grant);
        }

        onDeleteUserPermission({ key }) {
            if (!confirm("The permission override will be deleted immediately.")) {
                return;
            }
            const deleting = this.permissionGrantService.deleteUserPermission(this.user.EmployeeID, key);
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(deleting, "Deleting permission grant failed")
            );
            deleting.then(() => {
                this.userPermissions = this.userPermissions.filter(p => p.Description.Key !== key);
            });
        }

        _showEditUserPermissionModal(permissionGrant) {
            this.$uibModal.open({
                ...UserPermissionEditorModal,
                resolve: {
                    user: () => this.user,
                    permissionGrant: () => permissionGrant,
                    permissionDescriptions: () => this.permissionDescriptions
                }
            }).result
                .then(({ permissionGrant }) => {
                    //TODO: Reload data more gracefully to avoid losing the selected tab.
                    this.$state.reload();
                })
                .catch(ex => {
                    if (ex && (ex === "cancel" || ex.$value === "cancel")) {
                        return;
                    }
                    console.error(ex);
                })
        }

        reduceClaimsFromUserRoles() {
            return this.roles.filter(role => this.isUserGrantedRole(this.user, role))
                .reduce((claims, role) => {
                    switch (role.GrantType) {
                        case GrantType.GrantRole:
                            claims.push({ Type: roleClaimType, Value: role.RoleID });
                            break;
                        case GrantType.GrantAll:
                            claims.push({ Type: roleClaimType, Value: role.RoleID });
                            for (const claim of role.Claims) {
                                if (!claims.some(p => p.Type === claim.Type && p.Value === claim.Value)) {
                                    claims.push(claim);
                                }
                            }
                            break;
                        case GrantType.GrantClaims:
                            claims.push({ Type: roleClaimType, Value: role.RoleID });
                            for (const claim of role.Claims) {
                                if (!claims.some(p => p.Type === claim.Type && p.Value === claim.Value)) {
                                    claims.push(claim);
                                }
                            }
                            break;
                    }
                    return claims;
                }, []);
        }

        isUserGrantedRole(user, role) {
            if (role.Scope === "Divisional") {
                return this.user.RolesAtDivisions.some(p => p.RoleID === role.RoleID);
            } else if (role.Scope === "Organizational") {
                return this.user.RolesAtOffices.some(p => p.RoleID === role.RoleID);
            } else {
                return this.user.Roles.some(p => p === role.RoleID);
            }

        }

        /**
         * Return a patch representing security grants added or removed since the
         * component was loaded.
         */
        _generateSecurityPatch() {
            const deletedRoles = this.pristineSecurity.Roles.filter(p =>
                !this.user.Roles.some(q => q === p));
            const addedRoles = this.user.Roles.filter(p => !this.pristineSecurity.Roles.some(q => p === q));
            const deletedRolesAtDivisions = this.pristineSecurity.RolesAtDivisions
                .filter(p => !this.user.RolesAtDivisions.some(q => q.RoleID === p.RoleID
                    && q.DivisionID === p.DivisionID));
            const addedRolesAtDivisions = this.user.RolesAtDivisions
                .filter(p => !this.pristineSecurity.RolesAtDivisions.some(q => q.RoleID === p.RoleID
                    && q.DivisionID === p.DivisionID));
            const deletedRolesAtOffices = this.pristineSecurity.RolesAtOffices
                .filter(p => !this.user.RolesAtOffices.some(q => q.RoleID === p.RoleID
                    && q.IcdOfficeID === p.IcdOfficeID));
            const addedRolesAtOffices = this.user.RolesAtOffices
                .filter(p => !this.pristineSecurity.RolesAtOffices.some(q => q.RoleID === p.RoleID
                    && q.IcdOfficeID === p.IcdOfficeID));

            const patch = {};
            if (addedRoles.length > 0 || deletedRoles.length > 0) {
                patch.roles = {
                    added: addedRoles,
                    removed: deletedRoles
                };
            }
            if (addedRolesAtDivisions.length > 0 || deletedRolesAtDivisions.length > 0) {
                patch.rolesAtDivisions = {
                    added: addedRolesAtDivisions,
                    removed: deletedRolesAtDivisions
                };
            }
            if (addedRolesAtOffices.length > 0 || deletedRolesAtOffices.length > 0) {
                patch.rolesAtOffices = {
                    added: addedRolesAtOffices,
                    removed: deletedRolesAtOffices
                };
            }
            return patch;
        }
    }
}
